import { faFlag } from "@fortawesome/pro-solid-svg-icons"
import { Box, Grid, Typography } from "@mui/material"
import TextField from "@mui/material/TextField"
import { DatePicker } from "@mui/x-date-pickers"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import { StyledInputLabel } from "components/Form/FormComponents/FormSwitch/FormSwitch.styled"
import { Sidebar } from "components/sidedrawer/Sidebar2"
import { useFormikContext } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { t } from "i18next"
import moment from "moment"
import { useContext, useEffect, useRef, useState } from "react"
import * as Yup from "yup"

const expenseValidationSchema = Yup.object().shape({
  name: Yup.string().required(t("vehicleregistry.cost_name_is_required", "Cost name is required")),
  amount: Yup.number().positive(t("vehicleregistry.must_be_a_positive_number", "Must be a positive number")).required(t("vehicleregistry.cost_name_is_required", "Cost name is required"))
})

interface ExpenseSidebarProps {
  handleSubmit: () => void
  clickedExpense: any
  vehicleData: any
}

interface VehicleExpenseFormikValues {
  id: number
  name: string
  amount: number
  date: moment.Moment | null
  description: string
  vehicleId: number
}

const initialValues: VehicleExpenseFormikValues = {
  id: 0,
  name: "",
  amount: 0,
  date: null,
  description: "",
  vehicleId: 0
}

const ExpenseName = () => {
  const { values, handleChange, handleBlur, touched, errors } = useFormikContext<VehicleExpenseFormikValues>()

  return (
    <Grid item xs={12}>
      <StyledInputLabel>
        <Typography variant="h6" color="black">
          {t("vehicleregistry.cost", "Cost")}
        </Typography>
      </StyledInputLabel>
      <TextField
        placeholder={t("vehicleregistry.enter_cost", "Enter cost")}
        autoComplete="off"
        fullWidth
        id="name"
        value={values.name}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.name && errors.name ? true : false}
        helperText={touched.name && errors.name ? errors.name : null}
        variant="outlined"
      />
    </Grid>
  )
}

const ExpenseAmount = () => {
  const { values, handleChange, handleBlur, touched, errors } = useFormikContext<VehicleExpenseFormikValues>()

  return (
    <Grid item xs={12}>
      <StyledInputLabel>
        <Typography variant="h6" color="black">
          {t("global.amount", "Amount")}
        </Typography>
      </StyledInputLabel>
      <TextField
        placeholder={t("vehicleregistry.enter_amount_in_sek", "Enter amount in SEK")}
        autoComplete="off"
        fullWidth
        type="number"
        id="amount"
        value={values.amount}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.amount && errors.amount ? true : false}
        helperText={touched.amount && errors.amount ? errors.amount : null}
        variant="outlined"
      />
    </Grid>
  )
}

const ExpenseDate = () => {
  const { values, touched, errors, setFieldValue } = useFormikContext<VehicleExpenseFormikValues>()

  return (
    <Grid item xs={12}>
      <StyledInputLabel>
        <Typography variant="h6" color="black">
          {t("global.date", "Date")}
        </Typography>
      </StyledInputLabel>
      <DatePicker
        value={values.date ? moment(values.date) : null}
        format={"YYYY-MM-DD"}
        onChange={(value) => {
          setFieldValue("date", moment(value).isValid() ? moment(value).format("YYYY-MM-DD") : null)
        }}
        slotProps={{
          textField: {
            variant: "outlined",
            placeholder: t("vehicleregistry.date_of_purchase", "Date of purchase"),
            helperText: touched.date && errors.date ? errors.date : null,
            error: touched.date && errors.date ? true : false,
            sx: { width: "100%" }
          }
        }}
      />
    </Grid>
  )
}

const ExpenseDescription = () => {
  const { values, handleChange, handleBlur, touched, errors } = useFormikContext<VehicleExpenseFormikValues>()

  return (
    <Grid item xs={12}>
      <StyledInputLabel>
        <Typography variant="h6" color="black">
          {t("global.description", "Description")}
        </Typography>
      </StyledInputLabel>
      <TextField
        placeholder={t("vehicleregistry.describe_what_the_cost_applies_to", "Describe what the cost applies to")}
        multiline={true}
        rows={4}
        autoComplete="off"
        fullWidth
        id="description"
        value={values.description}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.description && errors.description ? true : false}
        helperText={touched.description && errors.description ? errors.description : null}
        variant="outlined"
      />
    </Grid>
  )
}

export const ExpenseSidebar = ({ vehicleData, clickedExpense, handleSubmit }: ExpenseSidebarProps) => {
  const [formikValues, setFormikValues] = useState(initialValues)
  const [expenseResult, expenseApi] = useApi() as [IApiResult, IApiFunc]
  const formRef = useRef() as any
  const appContext = useContext(AppContext)

  const handleFormikSubmit = (values: VehicleExpenseFormikValues) => {
    const formValues = {
      ...values,
      date: moment(values.date).add(12, "hours").utc(),
      vehicleId: vehicleData.id
    }

    if (values.id === 0) {
      const url = apiEndpoints.vehicleexpense.createvehicleexpense
      return expenseApi.post(url, formValues)
    }
    const url = apiEndpoints.vehicleexpense.updatevehicleexpense
    return expenseApi.put(url, formValues)
  }

  useEffect(() => {
    if (expenseResult.status !== 2) return
    handleSubmit()
    setFormikValues(initialValues)
    appContext.toggleSideBar(false)
  }, [expenseResult])

  useEffect(() => {
    if (clickedExpense === undefined) {
      setFormikValues(initialValues)
      formRef?.current?.resetForm()
    } else {
      setFormikValues({
        ...clickedExpense
      })
    }
  }, [clickedExpense])

  useEffect(() => {
    if (appContext.isSidebarOpen) return
    setFormikValues(initialValues)
  }, [appContext.isSidebarOpen])

  return (
    <Sidebar
      initialFormikValues={formikValues}
      setInitialFormikValues={setFormikValues}
      formResult={expenseResult}
      formData={{
        formId: "VehicleExpenseForm",
        subTitleNew: "",
        subTitleEdit: t("vehicleregistry.edit_cost", "Edit cost") + " " + formikValues?.name,
        submitEditText: t("global.save", "Save"),
        submitText: t("global.save", "Save"),
        icon: faFlag,
        titleEdit: t("vehicleregistry.edit_cost", "Edit cost"),
        titleNew: t("vehicleregistry.add_cost ", "Add cost"),
      }}
      handleSubmit={handleFormikSubmit}
      validationSchema={expenseValidationSchema}
      formRef={formRef}>
      <Box display="flex" flexDirection="column" gap="16px">
        <ExpenseName />
        <ExpenseAmount />
        <ExpenseDate />
        <ExpenseDescription />
      </Box>
    </Sidebar>
  )
}
